<template>
  <div>
    <div class="p-8 bg-gray-800">
      <div class="max-w-7xl mx-auto">
        <div class="md:flex md:items-center md:justify-between">
          <div class="flex-1 min-w-0">
            <h2 class="text-2xl font-bold leading-7 text-white sm:text-3xl sm:truncate">Orders</h2>
          </div>
          <div class="mt-4 flex md:mt-0 md:ml-4 space-x-2">
            <ExportAction :account-id="accountId" :query="query" />
            <WoocommerceApiServiceManager
              :account-id="accountId"
              :woocommerce-api-services="woocommerceApiServices"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="p-8">
      <div v-if="loading" class="text-center">
        <Loader />
      </div>
      <List v-else :orders="orders" :softone-api-services="softoneApiServices">
        <template #before-table="orderListProps">
          <div class="flex justify-between bg-white">
            <div class="w-2/3">
              <Search
                :action="searchOrders"
                @loading="orderListProps.setLoading()"
                @loaded="orderListProps.setLoading(false)"
              />
            </div>
            <div class="flex px-4 py-3 sm:px-6 space-x-2">
              <Filters
                :action="filterOrders"
                :filters="query.filter"
                @loading="orderListProps.setLoading()"
                @loaded="orderListProps.setLoading(false)"
              />
              <RefreshList
                :action="fetchOrders"
                @loading="orderListProps.setLoading()"
                @loaded="orderListProps.setLoading(false)"
              />
            </div>
          </div>
          <div v-show="hasFilters" class="px-4 py-3 sm:px-6 bg-white">
            <ActiveFilterList
              :action="filterOrders"
              :filters="query.filter"
              @loading="orderListProps.setLoading()"
              @loaded="orderListProps.setLoading(false)"
            />
          </div>
          <Pagination
            :meta="meta"
            :action="fetchPage"
            @loading="orderListProps.setLoading()"
            @loaded="orderListProps.setLoading(false)"
          />
        </template>
        <template #after-table="orderListProps">
          <Pagination
            :meta="meta"
            :action="fetchPage"
            @loading="orderListProps.setLoading()"
            @loaded="orderListProps.setLoading(false)"
          />
        </template>
      </List>
    </div>
  </div>
</template>

<script>
import ExportAction from './accOrders/components/export-action.vue';
import WoocommerceApiServiceManager from './accOrders/components/woocommerce-api-service-manager.vue';
import Search from './accOrders/components/search.vue';
import RefreshList from './accOrders/components/refresh-list.vue';
import Filters from './accOrders/components/filters.vue';
import ActiveFilterList from './accOrders/components/active-filter-list.vue';
import Pagination from './accOrders/components/pagination.vue';
import List from './accOrders/components/order-list.vue';

export default {
  components: {
    ExportAction,
    WoocommerceApiServiceManager,
    Search,
    RefreshList,
    Filters,
    ActiveFilterList,
    Pagination,
    List,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      meta: {},
      query: {
        page: 1,
        filter: {},
        include: 'contact,lineItemsSumQuantity,thirdPartyServiceDocuments',
        sort: '-date_created',
      },
      thirdPartyServiceIds: [],
    };
  },
  computed: {
    hasFilters() {
      return Object.keys(this.query.filter).length > 0;
    },
    queryFilters() {
      return Object.keys(this.query.filter).reduce((carry, filterName) => {
        const currentFilter = this.query.filter[filterName];

        const value =
          typeof currentFilter?.value !== 'undefined' ? currentFilter.value : currentFilter;

        return {
          ...carry,
          [filterName]: Array.isArray(value) ? value.join(',') : value,
        };
      }, {});
    },
    orders() {
      return this.$store.getters['ui/accountAccOrders/itemsByParent'](this.accountId);
    },
    thirdPartyServices() {
      return this.thirdPartyServiceIds
        .map((id) => this.$store.state.entities.thirdPartyServices.byId[id])
        .filter((tps) => tps);
    },
    woocommerceApiServices() {
      return this.thirdPartyServices.filter((tps) => tps.service === 'woocommerce');
    },
    softoneApiServices() {
      return this.thirdPartyServices.filter((tps) => tps.service === 'softone');
    },
  },
  created() {
    this.setup();
  },
  destroyed() {
    this.$store.dispatch('ui/accountAccOrders/clearByParent', { parentId: this.accountId });
  },
  methods: {
    setLoading(loading = true) {
      this.loading = Boolean(loading);
    },
    async setup() {
      if (this.loading) {
        return;
      }

      this.setLoading();

      try {
        await Promise.all([this.fetchOrders(), this.fetchThirdPartyServices()]);
      } catch (error) {
        // code here
      } finally {
        this.setLoading(false);
      }
    },
    async fetchThirdPartyServices() {
      try {
        const { thirdPartyServiceIds = [] } = await this.$store.dispatch(
          'ui/accountThirdPartyServices/load',
          {
            accountId: this.accountId,
            params: {
              filter: {
                service: 'woocommerce,softone',
                channel: 'api',
                active: true,
              },
            },
          }
        );

        this.thirdPartyServiceIds = thirdPartyServiceIds;
      } catch (error) {
        //
      }
    },
    async fetchOrders() {
      try {
        // eslint-disable-next-line no-unused-vars
        const { search, filter = {}, ...restQuery } = this.query;
        const { meta } = await this.$store.dispatch('ui/accountAccOrders/load', {
          id: this.accountId,
          query: {
            filter: {
              ...this.queryFilters,
              search,
            },
            ...restQuery,
          },
        });

        this.meta = meta;

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchPage(page) {
      this.query.page = page;
      await this.fetchOrders();
    },
    async filterOrders(filter) {
      this.query.filter = { ...filter };
      this.query.page = 1;
      await this.fetchOrders();
    },
    async searchOrders(search) {
      this.query.search = search;
      this.query.page = 1;
      await this.fetchOrders();
    },
  },
};
</script>
